import {
  HasloInsolitum,
  LogoInsolitum,
  LogoProduktu,
  SKALA_ZNAKU,
  cn,
  szerokoscZnaku,
} from '@insolitum/ui';
import { useTranslation } from 'react-i18next';

/**
 * Znak ekranów wejścia — logowanie, rejestracja (także partnera), „nie
 * pamiętasz hasła", ustawienie hasła, kod 2FA, zaproszenie. Jedno miejsce
 * zamiast kopii w każdym ekranie, bo te ekrany przechodzą jeden w drugi
 * linkami i znak nie ma prawa przy tym skakać ani zmieniać postaci.
 *
 * Znak stoi RAZEM z hasłem marki (CZUJE · ŁĄCZY · DZIAŁA) — hasło jest
 * częścią lockupu, nie treścią ekranu. Do 05.10.2026 hasło renderował
 * wyłącznie ekran logowania, więc przejście logowanie → rejestracja zmieniało
 * lockup, a na telefonie treść pod znakiem podskakiwała o 21 px (pierwszy
 * tekst na y 178 wobec 157). Treść hasła żyje w jednym kluczu i18n
 * (`auth.haslo`); szerokość = szerokość znaku co do piksela (`szerokoscZnaku`).
 *
 * Logowanie ma własny układ (panel hero i kolumna formularza), więc bierze
 * oba warianty osobno — `ZnakWejsciaPoziomy` w rogu panelu hero,
 * `ZnakWejsciaPionowy` nad formularzem na telefonie — ale z tego samego
 * pliku, więc nie może się rozjechać z resztą rodziny.
 */

/**
 * Telefon: układ pionowy `SKALA_ZNAKU.pionowe` na osi ekranu, w przepływie,
 * górną krawędzią 24 px od góry (padding rodzica). Hasło 16 px pod napisem
 * (pole ochronne pionowego 96 = 14,4 px w górę do siatki 4) i tej samej
 * szerokości co napis INSOLITUM.
 *
 * `relative z-10`: ziarno tła (`.aurora-grain::before`) jest warstwą
 * pozycjonowaną, więc nie pozycjonowany znak malowałby się POD nim.
 */
export function ZnakWejsciaPionowy({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('relative z-10 flex flex-col items-center', className)}>
      <LogoInsolitum uklad="pionowe" wysokosc={SKALA_ZNAKU.pionowe} className="mb-4" />
      <HasloInsolitum
        tekst={t('auth.haslo')}
        szerokosc={szerokoscZnaku('pionowe', SKALA_ZNAKU.pionowe)}
      />
    </div>
  );
}

/**
 * Desktop: `LogoProduktu „Universe"` H 45, a pod nim hasło — 12 px niżej
 * (pole ochronne poziomego 45 = 9,9 px w górę do siatki 4), lewą krawędzią
 * równo z sygnetem, szerokością całego znaku (sygnet + napis, bez nazwy
 * produktu za kreską). Szerokość znaku przy H 45 (167,43 px) równa się
 * szerokości napisu pionowego 96 (167,56 px), więc hasło ma to samo
 * rozstrzelenie na obu urządzeniach.
 *
 * `flex` zamiast `block`: blok z elementem `inline-flex` w środku dokłada
 * linię tekstu, która przesuwa logo o pół interlinii z (48, 48).
 */
export function ZnakWejsciaPoziomy({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('flex flex-col items-start gap-3', className)}>
      <LogoProduktu produkt="Universe" wysokosc={SKALA_ZNAKU.naglowekWejscia} />
      <HasloInsolitum
        tekst={t('auth.haslo')}
        szerokosc={szerokoscZnaku('poziome', SKALA_ZNAKU.naglowekWejscia)}
      />
    </div>
  );
}

/**
 * Oba warianty w miejscach rodziny: telefon w przepływie (24 px od góry, na
 * osi), desktop w rogu (48, 48) — dokładnie tam, gdzie stoi w panelu hero
 * logowania (`p-12`). Logo poziome NIE stoi na osi nad tytułem: jego masa
 * leży ~16 % szerokości w lewo od środka pudełka, więc na osi ekranu
 * wyglądałoby na przesunięte (ZNAK-W-SYSTEMIE §3 p. 9).
 *
 * Znak, nie kafel z napisem: słowo „Insolitum" wpisane krojem interfejsu
 * obok sygnetu byłoby fałszywym napisem — prawdziwy jest w samym logo.
 *
 * Róg to `div`, nie `span`: w środku stoi blok (`ZnakWejsciaPoziomy`),
 * a `span` dopuszcza wyłącznie treść frazową. Wygląd bez zmian — `display`
 * i tak ustawiają klasy (`hidden`, `lg:flex`).
 */
export function ZnakWejscia() {
  return (
    <>
      <ZnakWejsciaPionowy className="lg:hidden" />
      <div className="hidden lg:absolute lg:left-12 lg:top-12 lg:z-10 lg:flex">
        <ZnakWejsciaPoziomy />
      </div>
    </>
  );
}
